import { useMemo } from 'react';
import { useEventTheme } from '@/providers/EventThemeProvider';
import type { BullsharkAttributes } from '@suigar/suifrens-display-system';
import type { CapyAttributes } from '@suigar/suifrens-display-system';
import type { AccessoryMetadata } from '@suigar/suifrens-display-system';

type SuiFrenVariant = 'bullshark' | 'capy';

type VariantAttributes<TVariant extends SuiFrenVariant> = TVariant extends 'bullshark' ? BullsharkAttributes : CapyAttributes;

interface Options<TVariant extends SuiFrenVariant> {
	variant?: TVariant;
	attributes?: Partial<VariantAttributes<TVariant>>;
	accessories?: AccessoryMetadata[];
}

type Result<TVariant extends SuiFrenVariant> = {
	attributes: VariantAttributes<TVariant>;
	accessories: AccessoryMetadata[];
};

function mergeAccessories(eventAccessories?: AccessoryMetadata[], overrideAccessories?: AccessoryMetadata[]) {
	if (!eventAccessories?.length && !overrideAccessories?.length) {
		return [] as AccessoryMetadata[];
	}

	const byType = new Map<string, AccessoryMetadata>();
	overrideAccessories?.forEach((item) => {
		byType.set(item.type, item);
	});
	eventAccessories?.forEach((item) => {
		byType.set(item.type, item);
	});

	return Array.from(byType.values());
}

export function useEventSuiFrenAppearance<TVariant extends SuiFrenVariant = 'bullshark'>(options: Options<TVariant> = {}): Result<TVariant> {
	const { event } = useEventTheme();
	const variant = (options.variant ?? 'bullshark') as TVariant;
	const attributeOverrides = options.attributes;
	const accessoryOverrides = options.accessories;

	return useMemo(() => {
		const baseVariant = event.suifren[variant as SuiFrenVariant] as {
			attributes: VariantAttributes<TVariant>;
			accessories?: AccessoryMetadata[];
		};

		const mergedAttributes = {
			...baseVariant.attributes,
			...(attributeOverrides ?? {}),
		} as VariantAttributes<TVariant>;

		const mergedAccessories = mergeAccessories(baseVariant.accessories, accessoryOverrides);

		return {
			attributes: mergedAttributes,
			accessories: mergedAccessories,
		};
	}, [accessoryOverrides, attributeOverrides, event, variant]);
}
